/**
 * Global Settings Page
 *
 * Enterprise features:
 * - Settings persistence via API
 * - Real-time validation
 * - Danger zone with confirmations
 * - Optimistic updates
 */

import { useState, useEffect, useCallback } from 'react';
import Button from '../../components/common/Button';
import Icon from '../../components/common/Icon';
import Modal from '../../components/modals/Modal';
import { settingsApi } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { useQuery, useMutation } from '../../hooks/useDataFetch';
import './GlobalSettings.css';

const GlobalSettings = () => {
  const { showToast } = useToast();
  const [settings, setSettings] = useState({
    platformName: '',
    supportEmail: '',
    defaultLanguage: 'en',
    sessionTimeout: 30,
    mfaRequired: false,
    allowSignups: true,
    requireEmailVerification: true,
    passwordMinLength: 8,
    maxLoginAttempts: 5,
    lockoutDuration: 15,
  });
  const [hasChanges, setHasChanges] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [showPurgeModal, setShowPurgeModal] = useState(false);

  // Fetch settings
  const {
    data: settingsData,
    isLoading: loadingSettings,
    refetch: refetchSettings,
  } = useQuery(
    () => settingsApi.get(),
    {
      key: 'admin:settings',
      cacheTTL: 60000,
    }
  );

  // Update settings when data loads
  useEffect(() => {
    if (settingsData) {
      const data = settingsData.settings || settingsData.data || settingsData;
      setSettings(prev => ({
        ...prev,
        platformName: data.platformName || prev.platformName,
        supportEmail: data.supportEmail || prev.supportEmail,
        defaultLanguage: data.defaultLanguage || prev.defaultLanguage,
        sessionTimeout: data.sessionTimeout ?? prev.sessionTimeout,
        mfaRequired: data.mfaRequired ?? prev.mfaRequired,
        allowSignups: data.allowSignups ?? prev.allowSignups,
        requireEmailVerification: data.requireEmailVerification ?? prev.requireEmailVerification,
        passwordMinLength: data.passwordMinLength ?? prev.passwordMinLength,
        maxLoginAttempts: data.maxLoginAttempts ?? prev.maxLoginAttempts,
        lockoutDuration: data.lockoutDuration ?? prev.lockoutDuration,
      }));
      setHasChanges(false);
    }
  }, [settingsData]);

  // Save settings mutation
  const saveMutation = useMutation(
    (data) => settingsApi.update(data),
    {
      onSuccess: () => {
        showToast('Settings saved successfully', 'success');
        setHasChanges(false);
        refetchSettings();
      },
      onError: (error) => {
        showToast(error.message || 'Failed to save settings', 'error');
      },
    }
  );

  // Reset tenants mutation
  const resetTenantsMutation = useMutation(
    () => settingsApi.resetTenants(),
    {
      onSuccess: () => {
        showToast('All tenant settings have been reset', 'success');
        setShowResetModal(false);
      },
      onError: (error) => {
        showToast(error.message || 'Failed to reset tenant settings', 'error');
      },
    }
  );

  // Purge sessions mutation
  const purgeSessionsMutation = useMutation(
    () => settingsApi.purgeSessions(),
    {
      onSuccess: () => {
        showToast('All sessions have been purged', 'success');
        setShowPurgeModal(false);
      },
      onError: (error) => {
        showToast(error.message || 'Failed to purge sessions', 'error');
      },
    }
  );

  // Handlers
  const handleChange = useCallback((key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  }, []);

  const handleSave = useCallback(() => {
    saveMutation.mutate(settings);
  }, [settings, saveMutation]);

  const handleCancel = useCallback(() => {
    if (settingsData) {
      const data = settingsData.settings || settingsData.data || settingsData;
      setSettings(prev => ({
        ...prev,
        platformName: data.platformName || '',
        supportEmail: data.supportEmail || '',
        defaultLanguage: data.defaultLanguage || 'en',
        sessionTimeout: data.sessionTimeout ?? 30,
        mfaRequired: data.mfaRequired ?? false,
        allowSignups: data.allowSignups ?? true,
        requireEmailVerification: data.requireEmailVerification ?? true,
        passwordMinLength: data.passwordMinLength ?? 8,
        maxLoginAttempts: data.maxLoginAttempts ?? 5,
        lockoutDuration: data.lockoutDuration ?? 15,
      }));
    }
    setHasChanges(false);
    showToast('Changes discarded', 'info');
  }, [settingsData, showToast]);

  const handleResetTenants = useCallback(() => {
    resetTenantsMutation.mutate();
  }, [resetTenantsMutation]);

  const handlePurgeSessions = useCallback(() => {
    purgeSessionsMutation.mutate();
  }, [purgeSessionsMutation]);

  if (loadingSettings && !settingsData) {
    return (
      <div className="settings-page">
        <div className="page-header">
          <div>
            <h1 className="page-title">Global Settings</h1>
            <p className="page-subtitle">Configure platform-wide settings and defaults</p>
          </div>
        </div>
        <div className="settings-loading">
          <div className="section skeleton">
            <div className="skeleton-header">
              <div className="skeleton-line" style={{ width: '150px', height: '24px' }}></div>
            </div>
            <div className="settings-grid">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="form-group skeleton">
                  <div className="skeleton-line" style={{ width: '80px', marginBottom: '8px' }}></div>
                  <div className="skeleton-line" style={{ width: '100%', height: '40px' }}></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Global Settings</h1>
          <p className="page-subtitle">Configure platform-wide settings and defaults</p>
        </div>
        <div className="page-actions">
          <Button
            variant="secondary"
            onClick={handleCancel}
            disabled={!hasChanges || saveMutation.isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            icon="check"
            onClick={handleSave}
            disabled={!hasChanges || saveMutation.isLoading}
          >
            {saveMutation.isLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {/* Unsaved changes banner */}
      {hasChanges && (
        <div className="alert alert-warning">
          <Icon name="alert-circle" size="sm" />
          <span>You have unsaved changes</span>
        </div>
      )}

      {/* General Settings */}
      <section className="section">
        <div className="section-header">
          <h3 className="section-title">
            <Icon name="settings" size="md" />
            General
          </h3>
        </div>

        <div className="settings-grid">
          <div className="form-group">
            <label className="form-label">Platform Name</label>
            <input
              type="text"
              className="form-input"
              value={settings.platformName}
              onChange={(e) => handleChange('platformName', e.target.value)}
              placeholder="Enter platform name"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Support Email</label>
            <input
              type="email"
              className="form-input"
              value={settings.supportEmail}
              onChange={(e) => handleChange('supportEmail', e.target.value)}
              placeholder="support@example.com"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Default Language</label>
            <select
              className="form-select"
              value={settings.defaultLanguage}
              onChange={(e) => handleChange('defaultLanguage', e.target.value)}
            >
              <option value="en">English</option>
              <option value="es">Spanish</option>
              <option value="fr">French</option>
              <option value="de">German</option>
              <option value="zh">Chinese</option>
              <option value="ja">Japanese</option>
              <option value="pt">Portuguese</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Session Timeout (minutes)</label>
            <input
              type="number"
              className="form-input"
              value={settings.sessionTimeout}
              onChange={(e) => handleChange('sessionTimeout', parseInt(e.target.value) || 0)}
              min="5"
              max="1440"
            />
          </div>
        </div>
      </section>

      {/* Security Settings */}
      <section className="section">
        <div className="section-header">
          <h3 className="section-title">
            <Icon name="shield" size="md" />
            Security
          </h3>
        </div>

        <div className="settings-list">
          <div className="setting-item">
            <div className="setting-info">
              <div className="setting-label">Require MFA for all users</div>
              <div className="setting-description">Force multi-factor authentication for enhanced security</div>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={settings.mfaRequired}
                onChange={(e) => handleChange('mfaRequired', e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <div className="setting-label">Require email verification</div>
              <div className="setting-description">Users must verify their email before accessing the platform</div>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={settings.requireEmailVerification}
                onChange={(e) => handleChange('requireEmailVerification', e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <div className="setting-label">Allow public signups</div>
              <div className="setting-description">Allow new users to create accounts without invitation</div>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={settings.allowSignups}
                onChange={(e) => handleChange('allowSignups', e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
        </div>

        <div className="settings-grid mt-6">
          <div className="form-group">
            <label className="form-label">Minimum Password Length</label>
            <input
              type="number"
              className="form-input"
              value={settings.passwordMinLength}
              onChange={(e) => handleChange('passwordMinLength', parseInt(e.target.value) || 8)}
              min="6"
              max="32"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Max Login Attempts</label>
            <input
              type="number"
              className="form-input"
              value={settings.maxLoginAttempts}
              onChange={(e) => handleChange('maxLoginAttempts', parseInt(e.target.value) || 5)}
              min="3"
              max="10"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Lockout Duration (minutes)</label>
            <input
              type="number"
              className="form-input"
              value={settings.lockoutDuration}
              onChange={(e) => handleChange('lockoutDuration', parseInt(e.target.value) || 15)}
              min="5"
              max="60"
            />
          </div>
        </div>
      </section>

      {/* Danger Zone */}
      <section className="section danger-section">
        <div className="section-header">
          <h3 className="section-title danger">
            <Icon name="alert-triangle" size="md" />
            Danger Zone
          </h3>
        </div>

        <div className="danger-actions">
          <div className="danger-item">
            <div className="danger-info">
              <div className="danger-label">Reset all tenant settings</div>
              <div className="danger-description">This will reset all tenant configurations to defaults</div>
            </div>
            <Button
              variant="danger"
              size="sm"
              onClick={() => setShowResetModal(true)}
            >
              Reset All
            </Button>
          </div>

          <div className="danger-item">
            <div className="danger-info">
              <div className="danger-label">Purge all sessions</div>
              <div className="danger-description">Force logout all users across all tenants</div>
            </div>
            <Button
              variant="danger"
              size="sm"
              onClick={() => setShowPurgeModal(true)}
            >
              Purge Sessions
            </Button>
          </div>
        </div>
      </section>

      {/* Reset Confirmation Modal */}
      {showResetModal && (
        <Modal
          title="Confirm Reset"
          onClose={() => setShowResetModal(false)}
        >
          <div className="confirm-modal-content">
            <div className="confirm-warning">
              <Icon name="alert-triangle" size="xl" />
            </div>
            <p>Are you sure you want to reset all tenant settings?</p>
            <p className="confirm-warning-text">
              This action cannot be undone. All tenant configurations will be reset to their default values.
            </p>
            <div className="modal-actions">
              <Button variant="secondary" onClick={() => setShowResetModal(false)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleResetTenants}
                disabled={resetTenantsMutation.isLoading}
              >
                {resetTenantsMutation.isLoading ? 'Resetting...' : 'Yes, Reset All'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Purge Sessions Confirmation Modal */}
      {showPurgeModal && (
        <Modal
          title="Confirm Purge"
          onClose={() => setShowPurgeModal(false)}
        >
          <div className="confirm-modal-content">
            <div className="confirm-warning">
              <Icon name="alert-triangle" size="xl" />
            </div>
            <p>Are you sure you want to purge all sessions?</p>
            <p className="confirm-warning-text">
              This will force logout all users across all tenants immediately.
            </p>
            <div className="modal-actions">
              <Button variant="secondary" onClick={() => setShowPurgeModal(false)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handlePurgeSessions}
                disabled={purgeSessionsMutation.isLoading}
              >
                {purgeSessionsMutation.isLoading ? 'Purging...' : 'Yes, Purge All'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default GlobalSettings;
